/**
 * Clay, dusk, dark, matcha and ocean themes. One attribute on <html> (data-theme) flips every design
 * token; this module owns reading, applying, and remembering the choice.
 *
 * Order of truth: the person's saved choice, else their operating system
 * preference. The boot script in index.html applies the same rule before the
 * first paint so the page never flashes the wrong theme.
 */

export type Theme = 'clay' | 'dusk' | 'dark' | 'matcha' | 'ocean';

export const THEMES: Theme[] = ['clay', 'dusk', 'dark', 'matcha', 'ocean'];

export const THEME_STORAGE_KEY = 'tap-theme';

/** The rule, pure and testable: saved choice wins, else the OS preference. */
export function resolveInitialTheme(stored: string | null, prefersDark: boolean): Theme {
  if (
    stored === 'dark' ||
    stored === 'clay' ||
    stored === 'dusk' ||
    stored === 'matcha' ||
    stored === 'ocean'
  ) {
    return stored;
  }
  // Anything else — including 'light', which this app used to offer — falls
  // through to the rule below rather than being written to the document. An
  // unrecognised value would otherwise leave every switcher button reading
  // aria-pressed="false", so the UI shows nothing selected while the page
  // renders the base theme.
  // Dusk is never chosen for someone: it is a deliberate middle, and guessing
  // it from an OS preference that only says "dark" would override a choice the
  // person never made.
  return prefersDark ? 'dark' : 'clay';
}

/** What the document is showing right now. */
export function currentTheme(): Theme {
  const current = document.documentElement.dataset['theme'];
  return current === 'dark' || current === 'dusk' || current === 'matcha' || current === 'ocean'
    ? current
    : 'clay';
}

/**
 * Applies the theme to the document and remembers it. Storage can be
 * unavailable (private mode); the visual switch must still work.
 */
/**
 * Canvas colours, mirroring --surface-page in tokens.css.
 *
 * Duplicated here and in index.html on purpose: the boot script needs them
 * before any stylesheet is parsed, and this needs them when somebody switches
 * theme after the inline style has already been set. Leaving the inline style
 * pointing at the old theme's canvas would show through as a mismatched strip
 * wherever the page is taller than its content.
 */
export const THEME_CANVAS: Record<Theme, string> = {
  clay: '#f9f8f6',
  dusk: '#171923',
  dark: '#151517',
  matcha: '#f8f9f6',
  ocean: '#f5f7fa',
};

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
  document.documentElement.style.backgroundColor = THEME_CANVAS[theme];
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Remembering failed; the session keeps the chosen theme regardless.
  }
}
